import React, { useState } from "react";
import { Eye, Heart, Trash2, FlaskConical, Trophy, Inbox, MessageSquare, Package as PackageIcon, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useList } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PREFIX_LABEL = { innovation: "Innovation Lab", fabrics: "Fabric Development" };

export default function RndInsights() {
  const { canWrite } = useAuth();
  if (!canWrite("rnd")) return <EmptyState title="Not authorized" hint="R&D Insights is available to Admins and Production Managers." testid="rnd-unauthorized" />;
  return (
    <div>
      <PageHeader title="R&D Insights" subtitle="Buyer engagement analytics and incoming sample requests" testid="rnd-insights-header" />
      <Tabs defaultValue="trending">
        <TabsList className="mb-4">
          <TabsTrigger value="trending" data-testid="rnd-tab-trending"><Trophy className="w-4 h-4 mr-1.5" /> Trending</TabsTrigger>
          <TabsTrigger value="requests" data-testid="rnd-tab-requests"><Inbox className="w-4 h-4 mr-1.5" /> Requests Inbox</TabsTrigger>
        </TabsList>
        <TabsContent value="trending"><TrendingTab /></TabsContent>
        <TabsContent value="requests"><RequestsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function Leaderboard({ title, icon: Icon, rows, metric }) {
  return (
    <Card className="p-4" data-testid={`leaderboard-${metric}`}>
      <div className="flex items-center gap-2 font-semibold mb-3"><Icon className="w-4 h-4 text-primary" /> {title}</div>
      {!rows?.length ? <div className="text-sm text-muted-foreground py-6 text-center">No buyer activity tracked yet.</div> : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const val = metric === "views" ? r.views : r.favorites;
            return (
              <div key={`${r.prefix}-${r.item_id}`} className="flex items-center gap-3 border-b border-border/50 pb-2 last:border-0" data-testid={`trend-${metric}-row-${r.dev_id}`}>
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center font-num font-bold text-sm shrink-0", i < 3 ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground")}>{i + 1}</div>
                <div className="w-10 h-10 rounded bg-secondary overflow-hidden shrink-0 flex items-center justify-center">
                  {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-full h-full object-cover" /> : <FlaskConical className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-num text-muted-foreground">{r.dev_id} · {PREFIX_LABEL[r.prefix]}</div>
                  <div className="text-sm font-medium truncate">{r.dev_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-num font-bold text-lg text-primary flex items-center gap-1 justify-end">{metric === "views" ? <Eye className="w-4 h-4" /> : <Heart className="w-4 h-4 fill-red-500 text-red-500" />}{fmt(val)}</div>
                  <div className="text-[10px] text-muted-foreground">{metric === "views" ? `${fmt(r.unique_viewers)} buyers` : `${fmt(r.views)} views`}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function TrendingTab() {
  const { data, isLoading } = useList("rnd-trending", "/rnd/trending");
  if (isLoading) return <Loading />;
  if (!data?.total_tracked) return <Card className="p-8"><EmptyState title="No trends yet" hint="Once buyers start viewing and favouriting your published developments, they'll rank here." testid="rnd-trending-empty" /></Card>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Leaderboard title="Most Viewed" icon={Eye} rows={data.most_viewed} metric="views" />
      <Leaderboard title="Most Favorited" icon={Heart} rows={data.most_favorited} metric="favorites" />
    </div>
  );
}

function RequestsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [replyReq, setReplyReq] = useState(null);
  const { data: ov } = useList("rnd-req-ov", "/dev-requests/overview");
  const { data: rows, isLoading } = useList("rnd-requests", "/dev-requests", { status });

  const setReqStatus = async (id, s) => { try { await api.put(`/dev-requests/${id}`, { status: s }); qc.invalidateQueries({ queryKey: ["rnd-requests"] }); qc.invalidateQueries({ queryKey: ["rnd-req-ov"] }); toast.success("Updated"); } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this request?")) return; try { await api.delete(`/dev-requests/${id}`); qc.invalidateQueries({ queryKey: ["rnd-requests"] }); qc.invalidateQueries({ queryKey: ["rnd-req-ov"] }); toast.success("Deleted"); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        {[["Total", ov?.total], ["New", ov?.new], ["In Progress", ov?.in_progress], ["Resolved", ov?.resolved], ["Samples", ov?.samples], ["Questions", ov?.questions]].map(([l, v]) => (
          <Card key={l} className="p-3" data-testid={`req-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className="font-num font-bold text-2xl text-primary">{fmt(v || 0)}</div></Card>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40 h-9" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "new", "in_progress", "resolved"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : !rows?.length ? <Card className="p-8"><EmptyState title="No requests" hint="Sample requests and questions from buyers will appear here." testid="rnd-requests-empty" /></Card> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead>Development</TableHead><TableHead>Type</TableHead><TableHead>Buyer</TableHead>
              <TableHead>Message</TableHead><TableHead>Date</TableHead><TableHead className="w-40">Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                <TableCell><div className="text-[10px] font-num text-muted-foreground">{r.dev_id} · {PREFIX_LABEL[r.prefix]}</div><div className="font-medium text-sm">{r.dev_name}</div></TableCell>
                <TableCell><span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit", r.type === "sample" ? "bg-primary/12 text-primary" : "bg-secondary text-muted-foreground")}>{r.type === "sample" ? <PackageIcon className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}{r.type === "sample" ? "Sample" : "Question"}</span></TableCell>
                <TableCell><div className="text-sm font-medium">{r.buyer_name || r.buyer_email}</div>{r.buyer_company && <div className="text-xs text-muted-foreground">{r.buyer_company}</div>}</TableCell>
                <TableCell className="max-w-[240px]"><div className="text-sm text-muted-foreground truncate" title={r.message}>{r.message || "—"}</div></TableCell>
                <TableCell className="text-sm whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(s) => setReqStatus(r.id, s)}><SelectTrigger className="h-8 w-36" data-testid={`req-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["new", "in_progress", "resolved"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8 relative" onClick={() => setReplyReq(r)} data-testid={`req-reply-${r.id}`}><MessageCircle className="w-4 h-4" />{r.replies?.length > 0 && <span className="absolute -top-0.5 -right-0.5 text-[9px] font-bold bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center">{r.replies.length}</span>}</Button>
                  <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => del(r.id)} data-testid={`req-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        </Card>
      )}
      {replyReq && <ReplyDialog req={replyReq} onClose={() => setReplyReq(null)} />}
    </div>
  );
}

function ReplyDialog({ req, onClose }) {
  const qc = useQueryClient();
  const [thread, setThread] = useState(req.replies || []);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dev-requests/${req.id}/reply`, { message: msg });
      setThread((t) => [...t, data.reply]);
      setMsg("");
      qc.invalidateQueries({ queryKey: ["rnd-requests"] });
      qc.invalidateQueries({ queryKey: ["rnd-req-ov"] });
      toast.success("Reply sent to buyer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send reply"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="req-reply-dialog">
        <DialogHeader>
          <DialogTitle>Reply · {req.dev_id}</DialogTitle>
          <DialogDescription>{req.dev_name} — {req.type === "sample" ? "Sample request" : "Question"} from {req.buyer_name || req.buyer_email}{req.buyer_company ? ` (${req.buyer_company})` : ""}</DialogDescription>
        </DialogHeader>
        {req.message && <div className="bg-secondary/50 rounded-lg p-2.5 text-sm"><span className="text-muted-foreground text-xs">Buyer wrote:</span><div>{req.message}</div></div>}
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {thread.length === 0 ? <div className="text-sm text-muted-foreground">No replies yet. Send the first one below.</div> :
            thread.map((rp) => (
              <div key={rp.id} className="bg-primary/8 border border-primary/20 rounded-lg p-2.5" data-testid="req-reply-item">
                <div className="text-sm">{rp.message}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{rp.by} · {rp.at ? new Date(rp.at).toLocaleString() : ""}</div>
              </div>
            ))}
        </div>
        <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type your reply to the buyer…" data-testid="req-reply-input" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={send} disabled={busy || !msg.trim()} className="gap-1.5" data-testid="req-reply-send"><Send className="w-4 h-4" /> Send Reply</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
